import { Box, Typography, Card } from "@mui/material";
import { styled } from "@mui/material/styles";
import { translations } from "../translations";
const cardIssuanceImg = "/assets/card-issuance.webp";
const directBankTransfersImg = "/assets/direct-bank-transfers.webp";
const easyToUseImg = "/assets/easy-to-use.webp";
const appleGooglePlayImg = "/assets/apple-google-play.webp";
const lockIcon = "/assets/lock.svg";

const FeatureCard = styled(Card)(({ theme }) => ({
  background: "var(--overlay-very-light)",
  borderRadius: "40px",
  padding: theme.spacing(5),
  boxShadow: "none",
  height: "100%",
  width: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2.5),
    borderRadius: "20px",
    height: "auto",
  },
}));

const CardIssuanceImageContainer = styled(Box)({
  flexGrow: 1,
  position: "relative",
  margin: "0 -40px -40px -40px",
  borderRadius: "0 0 40px 40px",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const FeatureTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h3,
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
  },
}));

const FeatureDescription = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
}));

const CenteredContainer = styled(Box)({
  flexGrow: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const SaveMoneyContainer = styled(Box)(({ theme }) => ({
  textAlign: "center",
  paddingTop: "32px",
  paddingBottom: "32px",
  [theme.breakpoints.down("md")]: {
    paddingTop: "24px",
    paddingBottom: "24px",
  },
}));

const SaveMoneyTitle = styled(Typography)(({ theme }) => ({
  fontSize: "64px",
  fontWeight: 700,
  background:
    "linear-gradient(90deg, var(--green-gradient-start) 25.125%, var(--green-accent) 98.375%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  [theme.breakpoints.down("sm")]: {
    fontSize: "38px",
  },
}));

const SaveMoneySubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "44px",
  fontWeight: 700,
  background:
    "linear-gradient(90deg, var(--green-gradient-start) 25.125%, var(--green-accent) 98.375%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  [theme.breakpoints.down("sm")]: {
    fontSize: "26px",
  },
}));

const AppleGoogleContainer = styled(Box)({
  flexGrow: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "16px",
});

const ImageContainer = styled(Box)({
  flexGrow: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ImageLockContainer = styled(Box)({
  flexGrow: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  paddingTop: "24px",
  paddingBottom: "24px",
});

const LockIcon = styled("img")({
  width: "120px",
  height: "120px",
  opacity: 0.5,
});

const CardIssuanceCard = styled(FeatureCard)(({ theme }) => ({
  minHeight: "400px",
  [theme.breakpoints.up("md")]: {
    minHeight: "602px",
  },
}));

const EaseOfUseCard = styled(FeatureCard)(({ theme }) => ({
  minHeight: "350px",
  [theme.breakpoints.up("md")]: {
    minHeight: "536px",
  },
}));

const AppleGoogleCard = styled(FeatureCard)(({ theme }) => ({
  minHeight: "380px",
  [theme.breakpoints.up("md")]: {
    minHeight: "558px",
  },
}));

export default function FeaturesGrid() {
  const t = translations;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "repeat(2, minmax(0, 1fr))",
        },
        gap: { xs: "16px", md: "20px" },
        alignItems: "stretch",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: "16px", md: "20px" },
          height: "100%",
          minWidth: 0,
        }}
      >
        <CardIssuanceCard>
          <FeatureTitle variant="h4">{t.cardIssuance}</FeatureTitle>
          <FeatureDescription variant="body1">
            {t.cardIssuanceDesc}
          </FeatureDescription>
          <CardIssuanceImageContainer>
            <img
              src={cardIssuanceImg}
              alt="Card Issuance"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </CardIssuanceImageContainer>
        </CardIssuanceCard>
        <FeatureCard>
          <SaveMoneyContainer>
            <SaveMoneyTitle variant="h2">{t.saveUpTo}</SaveMoneyTitle>
            <SaveMoneySubtitle variant="h4">
              {t.onEveryTransaction}
            </SaveMoneySubtitle>
          </SaveMoneyContainer>
        </FeatureCard>
        <EaseOfUseCard
          sx={{
            flexGrow: { xs: 0, md: 1 },
          }}
        >
          <FeatureTitle variant="h4">{t.easeOfUse}</FeatureTitle>
          <FeatureDescription variant="body1">
            {t.easeOfUseDesc}
          </FeatureDescription>
          <CenteredContainer>
            <img
              src={easyToUseImg}
              alt="Easy to Use"
              style={{
                maxWidth: "100%",
                objectFit: "contain",
                borderRadius: "20px",
              }}
            />
          </CenteredContainer>
        </EaseOfUseCard>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: "16px", md: "20px" },
          height: "100%",
          minWidth: 0,
        }}
      >
        <AppleGoogleCard>
          <FeatureTitle variant="h4">{t.appleGooglePay}</FeatureTitle>
          <FeatureDescription variant="body1">
            {t.appleGooglePayDesc}
          </FeatureDescription>
          <AppleGoogleContainer>
            <img
              src={appleGooglePlayImg}
              alt="Apple and Google Pay"
              style={{
                maxHeight: "407px",
              }}
            />
          </AppleGoogleContainer>
        </AppleGoogleCard>
        <FeatureCard>
          <FeatureTitle variant="h4">{t.directBankTransfers}</FeatureTitle>
          <FeatureDescription variant="body1">
            {t.directBankTransfersDesc}
          </FeatureDescription>
          <ImageContainer>
            <img
              src={directBankTransfersImg}
              alt="Direct Bank Transfers"
              style={{
                maxWidth: "510px",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </ImageContainer>
        </FeatureCard>
        <FeatureCard
          sx={{
            flexGrow: { xs: 0, md: 1 },
          }}
        >
          <FeatureTitle variant="h4">{t.safetyReliability}</FeatureTitle>
          <FeatureDescription variant="body1">
            {t.safetyReliabilityDesc}
          </FeatureDescription>
          <ImageLockContainer>
            <LockIcon
              src={lockIcon}
              alt="Security Lock"
              style={{
                width: "182px",
                height: "182px",
                opacity: 0.5,
              }}
            />
          </ImageLockContainer>
        </FeatureCard>
      </Box>
    </Box>
  );
}
